import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import {
  Sparkles, Send, Loader2, ArrowRight, Check, CheckCircle2,
  AlertTriangle, Eye, RefreshCw,
} from 'lucide-react';

const CONF = {
  high: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-rose-100 text-rose-700 border-rose-200',
  manual: 'bg-indigo-100 text-indigo-700 border-indigo-200',
};

export const AstridApMapper = ({ custom, params, onConfirmed, basePath = '/payment-schedule/ap-format', savedLabel = 'AP file' }) => {
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [mapping, setMapping] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [status, setStatus] = useState('idle');
  const [transcript, setTranscript] = useState([]);
  const [input, setInput] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [sample, setSample] = useState(null);
  const [sampling, setSampling] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const scrollRef = useRef(null);
  const inFlight = useRef(false);

  const fileKey = custom?.filename + '|' + (custom?.uploaded_at || '');

  // Load the BillPortal field catalog so the user can override any mapping.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`${basePath}/catalog`, { params });
        if (alive) setCatalog(data.catalog || []);
      } catch { /* dropdown still works with just the current field */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath]);

  const changeField = (i, field) => {
    setMapping((prev) => prev.map((m, idx) => (idx === i ? {
      ...m,
      field: field || null,
      field_label: field ? (catalog.find((c) => c.field === field)?.description || null) : null,
      confidence: field ? 'manual' : 'low',
      manual: true,
    } : m)));
    setConfirmed(false);
    setSample(null);
  };

  const runAssistant = async (userMessage) => {
    if (inFlight.current) return; // never run two LLM calls at once (avoids origin overload / 502s)
    inFlight.current = true;
    setLoading(true);
    try {
      const body = { current_mapping: mapping.map((m) => ({ header: m.header, field: m.field })) };
      if (userMessage) body.user_message = userMessage;
      // LLM call: allow ample time and don't let the global retry loop storm a slow response.
      const { data } = await api.post(`${basePath}/assistant`, body, { params, timeout: 120000, _noRetry: true });
      setReply(data.reply || '');
      setMapping(data.mapping || []);
      setQuestions(data.questions || []);
      setStatus(data.status || 'needs_clarification');
      setTranscript((t) => [...t, { role: 'assistant', content: data.reply || '' }]);
      setSample(null);
      setConfirmed(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Astrid could not map the columns');
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  // Auto-run the initial mapping when a (new) file is uploaded.
  useEffect(() => {
    setTranscript([]);
    setMapping([]);
    setReply('');
    setQuestions([]);
    setStatus('idle');
    setSample(null);
    setConfirmed(false);
    if (custom?.filename) runAssistant(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileKey]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [transcript, loading]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput('');
    setTranscript((t) => [...t, { role: 'user', content: msg }]);
    await runAssistant(msg);
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      await api.post(`${basePath}/ai-confirm`,
        { mapping: mapping.map((m) => ({ header: m.header, field: m.field })) }, { params });
      setConfirmed(true);
      toast.success(`Mapping confirmed and saved as your custom ${savedLabel} format`);
      onConfirmed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save mapping');
    } finally {
      setConfirming(false);
    }
  };

  const showSample = async () => {
    setSampling(true);
    try {
      const { data } = await api.post(`${basePath}/ai-sample`,
        { mapping: mapping.map((m) => ({ header: m.header, field: m.field })) }, { params });
      setSample(data);
      if (!data.count) toast.info('No sample bills available for this organisation yet');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to build sample');
    } finally {
      setSampling(false);
    }
  };

  const unmappedCount = mapping.filter((m) => !m.field).length;

  return (
    <div className="mt-4 border border-indigo-200 rounded-none bg-indigo-50/40" data-testid="astrid-ap-mapper">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-indigo-200 bg-indigo-100/50">
        <Sparkles className="h-4 w-4 text-indigo-600" />
        <span className="font-semibold text-sm text-indigo-900">Astrid · Column mapping</span>
        {status === 'ready' && (
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-emerald-700" data-testid="astrid-status-ready">
            <CheckCircle2 className="h-3.5 w-3.5" /> All columns mapped
          </span>
        )}
        {status === 'needs_clarification' && (
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-amber-700" data-testid="astrid-status-needs">
            <AlertTriangle className="h-3.5 w-3.5" /> Needs your input
          </span>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Astrid reply */}
        {(reply || loading) && (
          <div className="flex gap-2 text-sm" data-testid="astrid-reply">
            <div className="h-7 w-7 shrink-0 rounded-full bg-indigo-600 text-white flex items-center justify-center">
              <Sparkles className="h-3.5 w-3.5" />
            </div>
            <div className="bg-white border border-indigo-100 rounded-lg px-3 py-2 text-foreground">
              {loading && !reply ? (
                <span className="inline-flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Astrid is reading your columns…
                </span>
              ) : reply}
            </div>
          </div>
        )}

        {/* Mapping table */}
        {mapping.length > 0 && (
          <div className="space-y-1.5">
          <div className="border border-border rounded-none overflow-hidden bg-white" data-testid="astrid-mapping-table">
            <div className="grid grid-cols-[1fr_auto_1.6fr_auto] gap-0 text-xs font-semibold bg-muted/60 px-3 py-2 border-b border-border">
              <span>Your column</span><span className="px-2"></span><span>Mapped BillPortal field</span><span>Confidence</span>
            </div>
            {mapping.map((m, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_1.6fr_auto] items-center gap-0 px-3 py-2 border-b border-border last:border-0 text-sm"
                data-testid={`astrid-map-row-${i}`}>
                <span className="font-medium truncate pr-2">{m.header}</span>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground mx-2" />
                <div className="min-w-0 pr-3">
                  <select
                    value={m.field || ''}
                    onChange={(e) => changeField(i, e.target.value)}
                    className="w-full max-w-full border border-border rounded-none px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    data-testid={`astrid-map-field-select-${i}`}
                  >
                    <option value="">— Leave blank (not matched) —</option>
                    {catalog.map((c) => (
                      <option key={c.field} value={c.field}>{c.field}</option>
                    ))}
                    {m.field && !catalog.some((c) => c.field === m.field) && (
                      <option value={m.field}>{m.field}</option>
                    )}
                  </select>
                  {m.field ? (
                    m.field_label && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{m.field_label}</p>
                  ) : (
                    <p className="text-[11px] text-amber-600 inline-flex items-center gap-1 mt-0.5">
                      <AlertTriangle className="h-3 w-3" /> Will be left blank
                    </p>
                  )}
                </div>
                <span className={`justify-self-end text-[10px] px-1.5 py-0.5 rounded-none border ${CONF[m.confidence] || 'bg-muted'}`}
                  data-testid={`astrid-map-conf-${i}`}>
                  {m.confidence}
                </span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground" data-testid="astrid-override-hint">
            Not quite right? Use the dropdown to change any mapping — even the high-confidence ones. Your choice is kept when you Confirm.
          </p>
          </div>
        )}

        {/* Astrid's questions */}
        {questions.length > 0 && (
          <div className="text-sm bg-amber-50 border border-amber-200 rounded-none p-3 space-y-1" data-testid="astrid-questions">
            <div className="font-medium text-amber-800 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> A few questions:
            </div>
            <ul className="list-disc list-inside text-amber-800 text-xs space-y-0.5">
              {questions.map((q, i) => <li key={i}>{q}</li>)}
            </ul>
          </div>
        )}

        {/* Chat transcript (user turns beyond the first) */}
        {transcript.filter((t) => t.role === 'user').length > 0 && (
          <div ref={scrollRef} className="max-h-40 overflow-y-auto space-y-2 text-sm" data-testid="astrid-transcript">
            {transcript.map((t, i) => (
              t.role === 'user' ? (
                <div key={i} className="flex justify-end">
                  <div className="bg-indigo-600 text-white rounded-lg px-3 py-1.5 max-w-[80%]">{t.content}</div>
                </div>
              ) : null
            ))}
          </div>
        )}

        {/* Chat input */}
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            placeholder="Explain a column to Astrid (e.g. 'Charges' means the amount we owe)…"
            className="flex-1 border border-border rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
            disabled={loading}
            data-testid="astrid-chat-input"
          />
          <Button type="button" variant="outline" size="sm" onClick={send} disabled={loading || !input.trim()} data-testid="astrid-send-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-indigo-200">
          <Button type="button" size="sm" onClick={confirm} disabled={confirming || loading || mapping.length === 0}
            data-testid="astrid-confirm-btn">
            {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            {unmappedCount > 0 ? `Confirm mapping (${unmappedCount} blank)` : 'Confirm mapping'}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={showSample} disabled={sampling || loading || mapping.length === 0}
            data-testid="astrid-sample-btn">
            {sampling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
            Show sample
          </Button>
          {confirmed && (
            <span className="text-xs text-emerald-700 inline-flex items-center gap-1.5" data-testid="astrid-confirmed-label">
              <CheckCircle2 className="h-3.5 w-3.5" /> Saved — your {savedLabel} will use this mapping.
            </span>
          )}
        </div>

        {/* Sample preview */}
        {sample && sample.count > 0 && (
          <div className="border border-border rounded-none overflow-x-auto bg-white" data-testid="astrid-sample-table">
            <div className="text-xs font-semibold px-3 py-2 border-b border-border bg-muted/60">
              Sample AP file · {sample.count} bill{sample.count === 1 ? '' : 's'}
            </div>
            <table className="text-xs w-full">
              <thead>
                <tr className="bg-muted/40">
                  {sample.headers.map((h, i) => <th key={i} className="text-left px-3 py-1.5 font-semibold whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {sample.rows.map((row, ri) => (
                  <tr key={ri} className="border-t border-border">
                    {row.map((cell, ci) => <td key={ci} className="px-3 py-1.5 whitespace-nowrap">{cell || <span className="text-muted-foreground">—</span>}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
